import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { T } from 'gt-react';
import { PlusIcon, SigmaIcon } from '@phosphor-icons/react';
import type {
  WorkbookColumnarSheet,
  WorkbookDefinition,
  WorkbookResult,
  WorkbookSource,
  WorkbookTable as TableData,
  WorkbookColumnType,
} from '@erp/shared-types';
import type { WorkbookPlan } from '@erp/workbooks';
import { Button } from '@/components/ui/button';
import { SheetEditor, type SheetPreview } from './SheetEditor';
import type { ChangeSheet } from './useWorkbookEditor';
import { sourcePreviewQuery } from './useSourcePreview';

type WorksheetPanelProps = {
  workbookId: string;
  sheet: WorkbookColumnarSheet;
  definition: WorkbookDefinition;
  plan: WorkbookPlan;
  result?: WorkbookResult;
  /** The Run of the shown Result; absent for a Live preview. */
  runId?: string;
  /** The input Worksheet's Result, when the input is a Worksheet. */
  inputResult?: WorkbookResult;
  stale: boolean;
  /** When the shown Result's Run was created. */
  ranAt?: string;
  preview?: SheetPreview;
  /** What fills cells without a Current or Live preview Result. */
  needsRunHint: string;
  runMenu?: ReactNode;
  readOnly: boolean;
  change: ChangeSheet;
  /** The types the input Source gives its columns, for Column settings to offer. */
  sourceTypes?: ReadonlyMap<string, WorkbookColumnType>;
  openItem: (id: string) => void;
  onNext: (input: { id: string; name: string }, table: TableData) => void;
  /** Adds a pivot table summarizing this Worksheet. */
  onPivot?: (input: { id: string; name: string }) => void;
};

export function WorksheetPanel(props: WorksheetPanelProps) {
  const source = props.definition.sources.find(
    (item) => item.id === props.sheet.inputId,
  );
  if (source && source.kind !== 'input')
    return <RemoteWorksheetPanel key={source.id} {...props} source={source} />;
  const input = source?.kind === 'input' ? source.table : props.inputResult;
  return <WorksheetView {...props} input={input} missingInput={!input} />;
}

function RemoteWorksheetPanel({
  source,
  ...props
}: WorksheetPanelProps & { source: WorkbookSource }) {
  const query = useQuery(sourcePreviewQuery(props.workbookId, source));
  return (
    <>
      {query.error && (
        <p role="alert" className="text-sm text-destructive">
          {query.error.message}
        </p>
      )}
      {query.isPending && (
        <p className="text-xs text-muted-foreground">
          <T context="workbook loading source">Loading source…</T>
        </p>
      )}
      <WorksheetView {...props} input={query.data} missingInput={false} />
    </>
  );
}

function WorksheetView({
  input,
  missingInput,
  ...props
}: WorksheetPanelProps & { input?: TableData; missingInput: boolean }) {
  return (
    <>
      {missingInput && (
        <p className="text-xs text-muted-foreground">
          <T context="worksheet upstream preview missing">
            Run the workbook to load the input worksheet preview.
          </T>
        </p>
      )}
      <SheetEditor
        key={props.sheet.inputId}
        workbookId={props.workbookId}
        runId={props.runId}
        sheet={props.sheet}
        openItem={props.openItem}
        definition={props.definition}
        plan={props.plan}
        input={input}
        result={props.result}
        stale={props.stale}
        ranAt={props.ranAt}
        preview={props.preview}
        needsRunHint={props.needsRunHint}
        runMenu={props.runMenu}
        readOnly={props.readOnly}
        change={props.change}
        sourceTypes={props.sourceTypes}
        onInputChange={(inputId) =>
          props.change((latest) => ({ ...latest, inputId }))
        }
      />
      <div className="flex items-center gap-1">
        {props.result && (
          <Button
            size="sm"
            variant="ghost"
            disabled={props.readOnly || props.stale}
            onClick={() => {
              if (props.result) props.onNext(props.sheet, props.result);
            }}
          >
            <PlusIcon />
            <T context="workbook next worksheet">Next worksheet</T>
          </Button>
        )}
        {props.onPivot && (
          <Button
            size="sm"
            variant="ghost"
            disabled={props.readOnly}
            onClick={() => props.onPivot?.(props.sheet)}
          >
            <SigmaIcon />
            <T context="workbook pivot this worksheet">Pivot table</T>
          </Button>
        )}
      </div>
    </>
  );
}
